import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Upload, Link as LinkIcon, X, Loader2, FileText, Camera, RefreshCw, Pencil, ChevronDown, ChevronUp, Lightbulb } from 'lucide-react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Checkbox } from '../components/ui/checkbox';
import { useUser } from '../contexts/UserContext';
import { isGuestVisitor } from '../utils/guestSession';
import { fileRejection, MENU_SURFACE } from '../utils/fileGate';
import { useLocation2 } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { UpgradeDrawer } from '../components/UpgradeDrawer';
import { CategoryReviewPanel } from '../components/CategoryReviewPanel';
import { createMenu, createGuestQuote, extractMenuText, extractMenuTextAsync, getCatalogs, uploadCatalogFile, getRestaurants, getRestaurant, createRestaurant, createContact, getStockQuotes, generateFromStockQuote, getDemoDistributor, getClassificationStatus, getQuotes, getMenuStatus } from '../services/api';
import type { CatalogSummary, RestaurantSummary, RestaurantDetail, StockQuoteResponse } from '../services/api';
import { isDemoMode, isLiquorDemo, demoType } from '../utils/demoMode';
import { isBuyerRole as checkBuyerRole } from '../utils/roles';

// --- Client-side menu file-type gate ---
// The <input accept="…"> attribute is only a file-picker hint, and drag-and-drop
// bypasses it entirely — so we guard programmatically here too. Copy mirrors the
// backend gate (MenuTextExtractorService) exactly so the message is identical
// whether the file is caught client-side or slips through to the server.
//
// The gate itself now lives in utils/fileGate.ts, shared with the token-gated
// catalog intake (TechLandingPage). The two surfaces accept different formats
// but must speak one vocabulary, so the copy lives there once and each surface
// supplies only what it accepts. This stays a named export because
// StartNewQuotePage.fileGate.test.ts pins this exact import.

// Returns plain-language rejection copy, or null if the file is accepted.
export const menuFileRejection = (file: { name: string; type: string }): string | null =>
  fileRejection(file, MENU_SURFACE);

// --- Types for ingredient editing ---
interface ParsedIngredient {
  id: string;
  name: string;
  confidence: number;
}

interface ParsedDish {
  id: string;
  name: string;
  ingredients: ParsedIngredient[];
}

// --- Simple client-side menu parser ---
function parseMenuText(text: string): ParsedDish[] {
  if (!text.trim()) return [];
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const dishes: ParsedDish[] = [];
  let currentDish: ParsedDish | null = null;
  let dishCounter = 0;
  let ingCounter = 0;

  for (const line of lines) {
    // Heuristic: lines that are short, don't contain commas, and look like headers → dish names
    const isHeader = line.length < 60 && !line.includes(',') && (
      /^[A-Z]/.test(line) || /^\d+[\.\)]\s/.test(line) || line.endsWith(':')
    );

    if (isHeader && line.length < 50) {
      dishCounter++;
      currentDish = {
        id: `dish-${dishCounter}`,
        name: line.replace(/[:]+$/, '').trim(),
        ingredients: [],
      };
      dishes.push(currentDish);
    } else {
      if (!currentDish) {
        dishCounter++;
        currentDish = {
          id: `dish-${dishCounter}`,
          name: 'Menu Items',
          ingredients: [],
        };
        dishes.push(currentDish);
      }
      // Split by commas or treat whole line as ingredient
      const parts = line.includes(',') ? line.split(',') : [line];
      for (const part of parts) {
        const name = part.trim().replace(/^[-•*]\s*/, '');
        if (name.length > 1 && name.length < 80) {
          ingCounter++;
          currentDish.ingredients.push({
            id: `ing-${ingCounter}`,
            name,
            confidence: 0.85,
          });
        }
      }
    }
  }
  return dishes;
}

function reconstructText(dishes: ParsedDish[]): string {
  return dishes.map(dish => {
    const header = dish.name !== 'Menu Items' ? dish.name + '\n' : '';
    const ings = dish.ingredients.map(i => i.name).join(', ');
    return header + ings;
  }).join('\n\n');
}

let nextIngId = 1000;
let nextDishId = 5000;

// B-01: an authenticated rep in customer mode must pick a customer before matching.
// B-109c: "Clear Results" must be disabled when there is nothing to clear.
// Pure-function helper so unit tests can verify the gate without mounting the page.
export function hasExtractedResults(params: {
  parsedDishCount: number;
  pasteText: string;
  menuPreviewText: string;
  hasUploadedFile: boolean;
}): boolean {
  const { parsedDishCount, pasteText, menuPreviewText, hasUploadedFile } = params;
  return parsedDishCount > 0 || pasteText.length > 0 || menuPreviewText.length > 0 || hasUploadedFile;
}

// Returns the inline error to show (and block on), or null when matching may proceed.
// Open Quote needs no customer by design; the guest/demo flow and buyer role
// (location-scoped) are out of scope for this guard.
export function customerSelectionError(params: {
  isAuthedRep: boolean;
  isQuoteOpened: boolean;
  isBuyerRole: boolean;
  hasCustomer: boolean;
}): string | null {
  const { isAuthedRep, isQuoteOpened, isBuyerRole, hasCustomer } = params;
  if (isAuthedRep && !isQuoteOpened && !isBuyerRole && !hasCustomer) {
    return 'Select or add a customer to begin matching.';
  }
  return null;
}

// C1 async extraction status interpretation. Pure + testable. Terminal states
// (per BE /menus/:id/status): "extracted" → done (extracted_text); "failed" →
// error (user_message); anything else ("extracting") → still polling.
export type ExtractionOutcome =
  | { kind: 'done'; text: string }
  | { kind: 'failed'; message: string }
  | { kind: 'pending' };

const EXTRACT_ASYNC_FAILED_COPY = "We couldn't read that menu. Try a different file, or paste the text instead.";

export function extractionOutcome(
  s: { status?: string; extracted_text?: string | null; user_message?: string | null } | null | undefined
): ExtractionOutcome {
  if (!s) return { kind: 'pending' };
  const status = (s.status || '').toLowerCase();
  if (status === 'extracted') return { kind: 'done', text: s.extracted_text || '' };
  if (status === 'failed') return { kind: 'failed', message: s.user_message || EXTRACT_ASYNC_FAILED_COPY };
  return { kind: 'pending' };
}

// Build 2 (FE creation half): the createMenu payload must carry the rep's
// SELECTED contact so the backend can stamp recipient_email at creation time,
// not only at send time. Prefer the primary contact among those selected
// (is_primary true); otherwise fall back to the first selected id; when
// nothing is selected, return undefined so the quote stays open (no
// contact_id key sent at all).
export function resolveContactIdForCreate(
  selectedContactIds: string[],
  contacts: { id: string; is_primary?: boolean }[]
): string | undefined {
  if (selectedContactIds.length === 0) return undefined;
  const selectedSet = new Set(selectedContactIds);
  const primarySelected = contacts.find((c) => selectedSet.has(c.id) && c.is_primary);
  return primarySelected ? primarySelected.id : selectedContactIds[0];
}

export function StartNewQuotePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAddRestaurantOpen, setIsAddRestaurantOpen] = useState(false);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [isAddGroupOpen, setIsAddGroupOpen] = useState(false);
  const [restaurants, setRestaurants] = useState<RestaurantSummary[]>([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState<RestaurantDetail | null>(null);
  const [restaurantsLoading, setRestaurantsLoading] = useState(false);
  const [isSavingRestaurant, setIsSavingRestaurant] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [menuPreviewText, setMenuPreviewText] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [conceptTipDismissed, setConceptTipDismissed] = useState(() => localStorage.getItem('quoteme_concept_tip_dismissed') === 'true');
  const [menuUrl, setMenuUrl] = useState('');
  const [isQuoteOpened, setIsQuoteOpened] = useState(isDemoMode());
  const [isUpgradeDrawerOpen, setIsUpgradeDrawerOpen] = useState(false);
  const { hasQuotesRemaining, quotesRemaining, profile, initGuestSession } = useUser();
  const { selectedLocation } = useLocation2();
  const { user } = useAuth();
  const isBuyerRole = checkBuyerRole(user?.role);
  // B-182: an authenticated user (rep / distributor_admin / quoteme_admin / chef —
  // anyone holding a real bearer token or a loaded user) is NEVER a guest, even
  // if a stale profile.isGuest flag lingers. The previous
  // `profile.isGuest || token===null` let that stale flag flip a logged-in rep
  // into the guest flow, which then created a guest session and overwrote their
  // identity with "Guest User" / "Guest Distributor" (silent session bleed).
  const isGuest = isGuestVisitor(user, localStorage.getItem('quoteme_token'));
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);
  const [serviceBusy, setServiceBusy] = useState(false);
  const [lastAction, setLastAction] = useState<'match' | 'skip' | null>(null);
  const [loadingPhase, setLoadingPhase] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Background processing state
  const [backgroundProcessing, setBackgroundProcessing] = useState(false);
  const [backgroundMenuId, setBackgroundMenuId] = useState<string | null>(null);
  const [backgroundAction, setBackgroundAction] = useState<'match' | 'skip' | null>(null);
  const backgroundPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const extractPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const actionErrorRef = useRef<HTMLDivElement>(null); // B-189 scroll target
  // Menu row anchored by C1's async extractions endpoint — threaded into
  // createMenu so it reuses that row (menu_id idempotency) instead of minting a
  // second Menu.
  const [extractedMenuId, setExtractedMenuId] = useState<string | null>(null);

  // Draft limit state (Fix 131 + Fix 139)
  const [draftLimitReached, setDraftLimitReached] = useState(false);

  // Catalog state
  const [catalogs, setCatalogs] = useState<CatalogSummary[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogUploading, setCatalogUploading] = useState(false);
  const [catalogUploadResult, setCatalogUploadResult] = useState<{ message: string; isError: boolean; catalogId?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quotaBlockMessage, setQuotaBlockMessage] = useState<string | null>(null);
  const [isDraggingCatalog, setIsDraggingCatalog] = useState(false);
  const catalogFileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [hasCamera, setHasCamera] = useState(false);
  const [catalogUploadExpanded, setCatalogUploadExpanded] = useState(
    !!(location.state as any)?.expandCatalog
  );
  const [showCategoryReview, setShowCategoryReview] = useState(false);
  const [uploadPhase, setUploadPhase] = useState<string | null>(null); // "uploading" | "imported" | "classifying" | "complete"
  const [classificationProgress, setClassificationProgress] = useState({ progress: 0, total: 0 });
  const [classificationFlagged, setClassificationFlagged] = useState(0);
  const classificationPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stock quotes
  const [stockQuotes, setStockQuotes] = useState<StockQuoteResponse[]>([]);

  // Parsed ingredients for right panel
  const [parsedDishes, setParsedDishes] = useState<ParsedDish[]>([]);
  const [editingIngredient, setEditingIngredient] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [addingToDish, setAddingToDish] = useState<string | null>(null);
  const [newIngredientName, setNewIngredientName] = useState('');
  const [addingNewDish, setAddingNewDish] = useState(false);
  const [newDishName, setNewDishName] = useState('');
  const [isExtractingPreview, setIsExtractingPreview] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  // Liquor demo state
  const liquorDemo = isLiquorDemo();
  const [demoDistributorId, setDemoDistributorId] = useState<string | null>(null);

  // Resolve liquor demo distributor on mount
  useEffect(() => {
    if (liquorDemo) {
      getDemoDistributor('liquor').then(res => {
        if (res.data) setDemoDistributorId(res.data.distributor_id);
      });
    }
  }, [liquorDemo]);

  // Fix 131: Check draft limit on mount for authenticated users
  // Skip for admins and users who enabled unlimited drafts
  useEffect(() => {
    if (isGuest) return;
    if (user?.unlimited_drafts) return;
    if (user?.role === 'quoteme_admin' || user?.role === 'distributor_admin') return;
    getQuotes({ status: 'draft' }).then(res => {
      if (res.data && res.data.length >= 2) {
        setDraftLimitReached(true);
      }
    });
  }, [isGuest, user?.unlimited_drafts, user?.role]);

  function cleanCatalogName(cat: CatalogSummary): string {
    const count = cat.row_count;
    if (count >= 1000) {
      return `Product Catalog (${(count / 1000).toFixed(count % 1000 === 0 ? 0 : 1)}k products)`;
    }
    return `Product Catalog (${count.toLocaleString()} products)`;
  }

  // Detect touch device with camera
  useEffect(() => {
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isTouch) return;
    navigator.mediaDevices?.enumerateDevices().then(devices => {
      const cam = devices.some(d => d.kind === 'videoinput');
      setHasCamera(cam);
    }).catch(() => {});
  }, []);

  // Loading phases
  useEffect(() => {
    if (!isCreatingQuote) { setLoadingPhase(0); return; }
    const delays = [4000, 4000, 4000, 4000, 30000, 0];
    let current = 0;
    let timeout: ReturnType<typeof setTimeout>;
    const advance = () => {
      if (current < 5) {
        current++;
        setLoadingPhase(current);
        if (delays[current] > 0) timeout = setTimeout(advance, delays[current]);
      }
    };
    timeout = setTimeout(advance, delays[0]);
    return () => clearTimeout(timeout);
  }, [isCreatingQuote]);

  // Background processing polling
  useEffect(() => {
    if (!backgroundProcessing || !backgroundMenuId) return;
    const poll = async () => {
      const res = await getMenuStatus(backgroundMenuId);
      if (res.data) {
        if (res.data.status === 'processed' && res.data.quote_id) {
          setBackgroundProcessing(false);
          setBackgroundMenuId(null);
          if (backgroundPollRef.current) clearInterval(backgroundPollRef.current);
          if (backgroundAction === 'skip') {
            navigate('/export-finalize', { state: { quoteId: res.data.quote_id, isOpenQuote: isQuoteOpened } });
          } else {
            navigate('/map-ingredients', { state: { quoteId: res.data.quote_id, menuId: backgroundMenuId, isOpenQuote: isQuoteOpened, locationId: isBuyerRole ? selectedLocation?.id : undefined } });
          }
        } else if (res.data.status === 'failed') {
          setBackgroundProcessing(false);
          setBackgroundMenuId(null);
          if (backgroundPollRef.current) clearInterval(backgroundPollRef.current);
          setError('We had trouble with this menu. Try breaking it into sections \u2014 paste just the dinner entr\u00e9es or cocktail list.');
        }
      }
    };
    backgroundPollRef.current = setInterval(poll, 5000);
    return () => {
      if (backgroundPollRef.current) clearInterval(backgroundPollRef.current);
    };
  }, [backgroundProcessing, backgroundMenuId, backgroundAction]);

  // Load catalogs, restaurants, stock quotes on mount
  useEffect(() => {
    const token = localStorage.getItem('quoteme_token');
    if (!token) return;
    setCatalogLoading(true);
    getCatalogs().then(res => { if (res.data) setCatalogs(res.data); setCatalogLoading(false); });
    setRestaurantsLoading(true);
    getRestaurants().then(res => { if (res.data) setRestaurants(res.data); setRestaurantsLoading(false); });
    getStockQuotes().then(res => { if (res.data) setStockQuotes(res.data); });
  }, []);

  // Auto-extraction: debounce paste text → parse into dishes/ingredients
  useEffect(() => {
    if (!pasteText.trim()) {
      setParsedDishes([]);
      return;
    }
    setIsExtractingPreview(true);
    const timer = setTimeout(() => {
      const stripped = stripPrices(pasteText);
      setMenuPreviewText(stripped);
      setParsedDishes(parseMenuText(stripped));
      setIsExtractingPreview(false);
    }, 1500);
    return () => { clearTimeout(timer); setIsExtractingPreview(false); };
  }, [pasteText]);

  // --- Handlers ---

  const stripPrices = (text: string): string => {
    return text
      .replace(/\$\d+(?:\.\d{1,2})?/g, '')
      .replace(/(?<=\s|^)\d{1,3}\.\d{2}(?=\s|$)/gm, '')
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/^\s+$/gm, '')
      .trim();
  };

  const handleFileSelect = (file: File) => {
    // Client-side gate FIRST — reject spreadsheets/docs/etc before touching any
    // state, so a bad drag-and-drop can't wipe an in-progress menu. Mirrors the
    // backend gate; drag-drop and "All files" picks bypass the <input accept>.
    const rejection = menuFileRejection(file);
    if (rejection) { setExtractError(rejection); return; }

    if (menuPreviewText && !confirm('You have a menu in progress. Replace it with this new file?')) return;
    setUploadedFile(file);
    setExtractError(null);
    // Create image preview URL for image files
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    if (file.type.startsWith('image/')) {
      setImagePreviewUrl(URL.createObjectURL(file));
    } else {
      setImagePreviewUrl(null);
    }
    const ext = file.name.toLowerCase();
    if (ext.endsWith('.csv') || ext.endsWith('.txt')) {
      setExtractedMenuId(null); // local read — not an async-anchored menu
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        setPasteText(text);
        const stripped = stripPrices(text);
        setMenuPreviewText(stripped);
        setParsedDishes(parseMenuText(stripped));
      };
      reader.readAsText(file);
    } else {
      handleFileExtract(file);
    }
  };

  const applyExtractedText = (text: string) => {
    const stripped = stripPrices(text);
    setPasteText(text);
    setMenuPreviewText(stripped);
    setParsedDishes(parseMenuText(stripped));
  };

  const stopExtractPoll = () => {
    if (extractPollRef.current) { clearInterval(extractPollRef.current); extractPollRef.current = null; }
  };

  // Authed users go through C1's async extraction endpoint (big PDFs don't hit
  // the 30s request timeout); guests stay on the synchronous path.
  const isAuthedExtraction = () => !(profile.isGuest || localStorage.getItem('quoteme_token') === null);

  const MAX_EXTRACT_POLLS = 90; // ~3.75 min at 2.5s

  // C1 async extraction: POST /menus/extractions (202 { menu_id }), poll
  // getMenuStatus until "extracted" (populate textarea) or "failed" (render
  // user_message verbatim). Anchors extractedMenuId for createMenu reuse.
  async function runAsyncExtraction(payload: { file?: File; url?: string }) {
    setIsExtracting(true);
    setExtractError(null);
    setIsExtractingPreview(true);
    setExtractedMenuId(null);
    stopExtractPoll();

    let kickoff;
    try {
      kickoff = await extractMenuTextAsync(payload);
    } catch (e: any) {
      setExtractError(e?.message || "We couldn't start reading that menu. Please try again.");
      setIsExtracting(false); setIsExtractingPreview(false);
      return;
    }
    if (kickoff.error || !kickoff.data?.menu_id) {
      setExtractError(isServiceBusyError(kickoff.error)
        ? 'Our menu analysis service is temporarily busy. Please try again in a few seconds.'
        : (kickoff.error || "We couldn't start reading that menu. Please try again."));
      setIsExtracting(false); setIsExtractingPreview(false);
      return;
    }

    const menuId = kickoff.data.menu_id;
    setExtractedMenuId(menuId);
    let attempts = 0;
    const finish = () => { stopExtractPoll(); setIsExtracting(false); setIsExtractingPreview(false); };

    extractPollRef.current = setInterval(async () => {
      attempts += 1;
      const res = await getMenuStatus(menuId);
      const outcome = extractionOutcome(res.data);
      if (outcome.kind === 'done') {
        finish();
        if (outcome.text) applyExtractedText(outcome.text);
        else setExtractError("We couldn't find any menu text in that file. Try a clearer photo, or paste the menu text instead.");
        return;
      }
      if (outcome.kind === 'failed') {
        finish();
        setExtractError(outcome.message);
        return;
      }
      if (attempts >= MAX_EXTRACT_POLLS) {
        finish();
        setExtractError("This is taking longer than expected. Please try again, or paste the menu text instead.");
      }
    }, 2500);
  }

  async function handleFileExtract(file: File) {
    if (isAuthedExtraction()) { await runAsyncExtraction({ file }); return; }

    // Guest sync path (unchanged).
    setIsExtracting(true);
    setExtractError(null);
    setIsExtractingPreview(true);
    try {
      const res = await extractMenuText({ file });
      if (res.error) {
        setExtractError(isServiceBusyError(res.error)
          ? 'Our menu analysis service is temporarily busy. Please try again in a few seconds.'
          : res.error);
      } else if (res.data?.text) {
        applyExtractedText(res.data.text);
      }
    } catch (e: any) {
      setExtractError(e.message || 'Failed to extract text');
    } finally {
      setIsExtracting(false);
      setIsExtractingPreview(false);
    }
  }

  async function handleUrlExtract() {
    if (!menuUrl.trim()) return;
    let url = menuUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) url = 'https://' + url;

    if (isAuthedExtraction()) { await runAsyncExtraction({ url }); return; }

    setIsExtracting(true);
    setExtractError(null);
    setIsExtractingPreview(true);
    try {
      const res = await extractMenuText({ url });
      if (res.error) {
        const err = res.error;
        if (isServiceBusyError(err)) {
          setExtractError('Our menu analysis service is temporarily busy. Please try again in a few seconds.');
        } else if (err.includes('pdf_too_large')) {
          setExtractError("This menu is too large to process at once. Try uploading just the section you need: dinner entrees, cocktail list, or appetizers.");
        } else if (err.includes('url_fetch_failed')) {
          setExtractError("We couldn't fetch that URL. Check the link or try uploading the PDF directly.");
        } else if (err.includes('url_unsupported_type')) {
          setExtractError("That URL didn't return a menu we can read. Try uploading the PDF directly.");
        } else {
          setExtractError(err);
        }
      } else if (res.data?.text) {
        const stripped = stripPrices(res.data.text);
        setPasteText(res.data.text);
        setMenuPreviewText(stripped);
        setParsedDishes(parseMenuText(stripped));
      }
    } catch (e: any) {
      setExtractError(e.message || 'Failed to extract text from URL');
    } finally {
      setIsExtracting(false);
      setIsExtractingPreview(false);
    }
  }

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  function isServiceBusyError(errorStr: string | undefined): boolean {
    return errorStr === 'service_busy' || (errorStr?.includes('service_busy') ?? false);
  }

  const handleRetry = () => {
    setServiceBusy(false);
    if (lastAction === 'match') handleContinueToQuoteBuilder();
    else if (lastAction === 'skip') handleSkipToExport();
  };

  // Catalog upload
  const startClassificationPolling = (catalogId: string) => {
    if (classificationPollRef.current) clearInterval(classificationPollRef.current);
    setUploadPhase('classifying');
    classificationPollRef.current = setInterval(async () => {
      const statusRes = await getClassificationStatus(catalogId);
      if (statusRes.data) {
        setClassificationProgress({ progress: statusRes.data.progress, total: statusRes.data.total });
        if (statusRes.data.status === 'complete') {
          if (classificationPollRef.current) clearInterval(classificationPollRef.current);
          classificationPollRef.current = null;
          setUploadPhase('complete');
          setClassificationFlagged(statusRes.data.flagged_count);
        }
      }
    }, 2000);
  };

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (classificationPollRef.current) clearInterval(classificationPollRef.current);
      if (extractPollRef.current) clearInterval(extractPollRef.current);
    };
  }, []);

  // B-189 — when an action error appears (e.g. "Select a customer"), bring it
  // into view so the user sees it at the button, not just in the top banner.
  useEffect(() => {
    if (error && actionErrorRef.current) {
      actionErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const handleCatalogFileSelect = async (file: File) => {
    const ext = file.name.toLowerCase();
    if (!ext.endsWith('.csv') && !ext.endsWith('.xlsx')) {
      setCatalogUploadResult({ message: 'Unsupported file type. Please upload a CSV or Excel file.', isError: true });
      return;
    }
    setCatalogUploading(true);
    setCatalogUploadResult(null);
    setUploadPhase('uploading');
    setClassificationProgress({ progress: 0, total: 0 });
    setClassificationFlagged(0);
    if (isGuest && !localStorage.getItem('quoteme_guest_token')) await initGuestSession();
    const res = await uploadCatalogFile(file);
    if (res.error) {
      setCatalogUploadResult({ message: res.error, isError: true });
      setUploadPhase(null);
    } else if (res.data) {
      setCatalogUploadResult({
        message: res.data.message,
        isError: false,
        catalogId: res.data.id
      });
      if (!isGuest) {
        const listRes = await getCatalogs();
        if (listRes.data) setCatalogs(listRes.data);
      } else {
        setCatalogs([{ id: res.data.id, status: 'active', row_count: res.data.item_count, created_at: new Date().toISOString() } as CatalogSummary]);
      }

      // Start polling if AI classification is needed
      if (res.data.classification_status === 'pending') {
        setClassificationProgress({ progress: 0, total: res.data.classification_total || 0 });
        startClassificationPolling(res.data.id);
      } else {
        setUploadPhase('complete');
      }
    }
    setCatalogUploading(false);
  };

  const handleCatalogDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDraggingCatalog(true); };
  const handleCatalogDragLeave = () => setIsDraggingCatalog(false);
  const handleCatalogDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDraggingCatalog(false);
    const file = e.dataTransfer.files[0];
    if (file) handleCatalogFileSelect(file);
  };

  // Ingredient editing
  const handleRemoveIngredient = (dishId: string, ingId: string) => {
    setParsedDishes(prev => prev.map(d =>
      d.id === dishId ? { ...d, ingredients: d.ingredients.filter(i => i.id !== ingId) } : d
    ));
  };

  const handleStartEdit = (ingId: string, name: string) => {
    setEditingIngredient(ingId);
    setEditValue(name);
  };

  const handleSaveEdit = (dishId: string, ingId: string) => {
    if (!editValue.trim()) return;
    setParsedDishes(prev => prev.map(d =>
      d.id === dishId ? {
        ...d,
        ingredients: d.ingredients.map(i => i.id === ingId ? { ...i, name: editValue.trim() } : i)
      } : d
    ));
    setEditingIngredient(null);
    setEditValue('');
  };

  const handleAddIngredient = (dishId: string) => {
    if (!newIngredientName.trim()) {
      // Empty enter = done adding, close the input
      setAddingToDish(null);
      setNewIngredientName('');
      return;
    }
    nextIngId++;
    setParsedDishes(prev => prev.map(d =>
      d.id === dishId ? {
        ...d,
        ingredients: [...d.ingredients, { id: `ing-new-${nextIngId}`, name: newIngredientName.trim(), confidence: 0.85 }]
      } : d
    ));
    setNewIngredientName('');
    // Keep input open for continuous entry — don't close addingToDish
  };

  const handleAddDish = () => {
    if (!newDishName.trim()) {
      setAddingNewDish(false);
      setNewDishName('');
      return;
    }
    nextDishId++;
    const dishId = `dish-new-${nextDishId}`;
    setParsedDishes(prev => [...prev, { id: dishId, name: newDishName.trim(), ingredients: [] }]);
    setNewDishName('');
    setAddingNewDish(false);
    // Auto-open ingredient input for the new dish
    setAddingToDish(dishId);
  };

  // Quote creation
  const handleContinueToQuoteBuilder = async () => {
    const menuText = parsedDishes.length > 0 ? reconstructText(parsedDishes) : (pasteText || menuPreviewText);
    if (!menuText) { setError('Please paste or parse menu text before continuing.'); return; }

    // B-01: block + inline message instead of firing a request that 422s silently.
    const isAuthedRep = !(profile.isGuest || localStorage.getItem('quoteme_token') === null);
    const customerError = customerSelectionError({
      isAuthedRep,
      isQuoteOpened,
      isBuyerRole,
      hasCustomer: !!selectedRestaurant,
    });
    if (customerError) { setError(customerError); return; }

    if (!hasQuotesRemaining()) { setIsUpgradeDrawerOpen(true); return; }

    setIsCreatingQuote(true);
    setServiceBusy(false);
    setLastAction('match');
    setError(null);
    try {
      if (profile.isGuest || localStorage.getItem('quoteme_token') === null) {
        if (!localStorage.getItem('quoteme_guest_token')) await initGuestSession();
        if (!localStorage.getItem('quoteme_guest_token')) {
          setError('Failed to start guest session. Please try again.');
          setIsCreatingQuote(false);
          return;
        }
        const payload = { raw_text: menuText, name: selectedRestaurant?.name || 'New Quote' };
        const distId = demoDistributorId || undefined;
        let response = await createGuestQuote(payload, distId);
        if (response.error && (response.error.includes('401') || response.error.includes('expired') || response.error.includes('not found') || response.error.includes('Session'))) {
          localStorage.removeItem('quoteme_guest_token');
          await initGuestSession();
          if (!localStorage.getItem('quoteme_guest_token')) {
            setError('Failed to start guest session. Please try again.');
            setIsCreatingQuote(false);
            return;
          }
          response = await createGuestQuote(payload, distId);
        }
        if (response.error) {
          if (isServiceBusyError(response.error)) { setServiceBusy(true); return; }
          if (response.error.includes('2 quotes in progress')) { setDraftLimitReached(true); return; }
          setError(`Failed to create quote: ${response.error}`);
          return;
        }
        if (response.data) {
          // Track 22: quote is processing async — navigate to chef/status to show
          // real pipeline stages. "Match to Catalog" must still land the guest on
          // the review/map-ingredients step-through once processing completes (the
          // authed rep branch below does this directly) — carry that intent through
          // as router state so ChefStatusPage knows to redirect there instead of the
          // read-only receipt page it defaults to (see ChefStatusPage.getStatusCompleteTarget).
          navigate(`/chef/status/${response.data.quote_id}`, { state: { completionTarget: 'map-ingredients' } });
        }
      } else {
        const response = await createMenu({ raw_text: menuText, name: isQuoteOpened ? 'Open Quote' : (selectedRestaurant?.name || 'New Quote'), restaurant_id: selectedRestaurant?.id, menu_id: extractedMenuId ?? undefined, contact_id: resolveContactIdForCreate(selectedContactIds, selectedRestaurant?.contacts || []), open_quote: isQuoteOpened });
        if (response.error) {
          if (isServiceBusyError(response.error)) { setServiceBusy(true); return; }
          if (response.error.includes('2 quotes in progress')) { setDraftLimitReached(true); return; }
          if (response.status === 402 || (response as any).error_code === 'free_quotes_exhausted') {
            setQuotaBlockMessage((response as any).error_data?.message || response.error);
            return;
          }
          setError(`Failed to create quote: ${response.error}`);
          return;
        }
        if (response.data) {
          if ((response.data as any).background) {
            setIsCreatingQuote(false);
            setBackgroundProcessing(true);
            setBackgroundMenuId(response.data.menu_id);
            setBackgroundAction('match');
          } else {
            navigate('/map-ingredients', {
              state: { quoteId: response.data.quote_id, menuId: response.data.menu_id, isOpenQuote: isQuoteOpened, locationId: isBuyerRole ? selectedLocation?.id : undefined }
            });
          }
        }
      }
    } catch (error) {
      setError('Failed to create quote. Please try again.');
    } finally {
      setIsCreatingQuote(false);
    }
  };

  const handleSkipToExport = async () => {
    const menuText = parsedDishes.length > 0 ? reconstructText(parsedDishes) : (pasteText || menuPreviewText);
    if (!menuText) { setError('Please paste or parse menu text before skipping.'); return; }
    if (!hasQuotesRemaining()) { setIsUpgradeDrawerOpen(true); return; }

    setIsCreatingQuote(true);
    setServiceBusy(false);
    setLastAction('skip');
    setError(null);
    try {
      if (profile.isGuest || localStorage.getItem('quoteme_token') === null) {
        if (!localStorage.getItem('quoteme_guest_token')) await initGuestSession();
        if (!localStorage.getItem('quoteme_guest_token')) {
          setError('Failed to start guest session. Please try again.');
          setIsCreatingQuote(false);
          return;
        }
        const skipDistId = demoDistributorId || undefined;
        let response = await createGuestQuote({ raw_text: menuText, name: selectedRestaurant?.name || 'New Quote' }, skipDistId);
        if (response.error && (response.error.includes('401') || response.error.includes('expired') || response.error.includes('not found') || response.error.includes('Session'))) {
          localStorage.removeItem('quoteme_guest_token');
          await initGuestSession();
          if (!localStorage.getItem('quoteme_guest_token')) {
            setError('Failed to start guest session. Please try again.');
            setIsCreatingQuote(false);
            return;
          }
          response = await createGuestQuote({ raw_text: menuText, name: selectedRestaurant?.name || 'New Quote' }, skipDistId);
        }
        if (response.error) {
          if (isServiceBusyError(response.error)) { setServiceBusy(true); return; }
          if (response.error.includes('2 quotes in progress')) { setDraftLimitReached(true); return; }
          setError(`Failed to create quote: ${response.error}`);
          return;
        }
        if (response.data) {
          // Track 22: quote is processing async — navigate to chef/status to show
          // real pipeline stages. "Skip to Export" is meant to skip the review
          // step (that's its whole purpose) and land on /export-finalize once
          // processing completes, exactly as the authed rep branch below does
          // immediately — 0ac243b left this hardcoded to the read-only receipt
          // page instead. Carry the original target through as router state
          // (see ChefStatusPage.getStatusCompleteTarget) so it's restored, not
          // reinvented.
          navigate(`/chef/status/${response.data.quote_id}`, { state: { completionTarget: 'export-finalize' } });
        }
      } else {
        const response = await createMenu({ raw_text: menuText, name: isQuoteOpened ? 'Open Quote' : (selectedRestaurant?.name || 'New Quote'), restaurant_id: selectedRestaurant?.id, menu_id: extractedMenuId ?? undefined, contact_id: resolveContactIdForCreate(selectedContactIds, selectedRestaurant?.contacts || []), open_quote: isQuoteOpened });
        if (response.error) {
          if (isServiceBusyError(response.error)) { setServiceBusy(true); return; }
          if (response.error.includes('2 quotes in progress')) { setDraftLimitReached(true); return; }
          if (response.status === 402 || (response as any).error_code === 'free_quotes_exhausted') {
            setQuotaBlockMessage((response as any).error_data?.message || response.error);
            return;
          }
          setError(`Failed to create quote: ${response.error}`);
          return;
        }
        if (response.data) {
          if ((response.data as any).background) {
            setIsCreatingQuote(false);
            setBackgroundProcessing(true);
            setBackgroundMenuId(response.data.menu_id);
            setBackgroundAction('skip');
          } else {
            navigate('/export-finalize', { state: { quoteId: response.data.quote_id, isOpenQuote: isQuoteOpened } });
          }
        }
      }
    } catch (error) {
      setError('Failed to create quote. Please try again.');
    } finally {
      setIsCreatingQuote(false);
    }
  };

  const handleRestaurantChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value === 'add-new') {
      setIsAddRestaurantOpen(true);
      e.target.value = '';
    } else if (e.target.value) {
      const res = await getRestaurant(e.target.value);
      if (res.data) {
        setSelectedRestaurant(res.data);
        // Auto-select the contact when there's only one
        if (res.data.contacts.length === 1) {
          setSelectedContactIds([res.data.contacts[0].id]);
        } else {
          setSelectedContactIds([]);
        }
      } else {
        setSelectedRestaurant(null);
        setSelectedContactIds([]);
      }
    } else {
      setSelectedRestaurant(null);
      setSelectedContactIds([]);
    }
  };

  // H4: the "Save Restaurant" button used to be a no-op (just closed the
  // drawer), so Customer Information never populated. Wire it to actually
  // persist the restaurant + primary contact, then select it (mirrors
  // handleRestaurantChange) so the Customer Information panel fills in.
  const handleSaveRestaurant = async () => {
    const fieldValue = (id: string) => {
      const el = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      return el?.value.trim() ?? '';
    };

    const name = fieldValue('new-restaurant-name');
    if (!name) {
      setError('Restaurant name is required.');
      return;
    }

    setIsSavingRestaurant(true);
    setError(null);
    try {
      const created = await createRestaurant({
        name,
        address_line_1: fieldValue('new-street-address') || undefined,
        city: fieldValue('new-city') || undefined,
        state: fieldValue('new-state') || undefined,
        zip: fieldValue('new-zip') || undefined,
      });
      if (!created.data) {
        setError('Failed to save restaurant. Please try again.');
        return;
      }
      const restaurantId = created.data.id;

      // Create the primary contact if any contact detail was entered.
      const contactName = fieldValue('new-contact-name');
      const email = fieldValue('new-email');
      const phone = fieldValue('new-phone');
      if (contactName || email || phone) {
        const [firstName, ...lastParts] = contactName.split(/\s+/);
        await createContact(restaurantId, {
          first_name: firstName || contactName || email || 'Contact',
          last_name: lastParts.join(' '),
          email: email || undefined,
          phone: phone || undefined,
          is_primary: true,
        });
      }

      // Refresh the dropdown and select the new restaurant so Customer
      // Information populates (same path as handleRestaurantChange).
      const listRes = await getRestaurants();
      if (listRes.data) setRestaurants(listRes.data);
      const detail = await getRestaurant(restaurantId);
      if (detail.data) {
        setSelectedRestaurant(detail.data);
        setSelectedContactIds(detail.data.contacts.length === 1 ? [detail.data.contacts[0].id] : []);
      }
      setIsAddRestaurantOpen(false);
    } catch {
      setError('Failed to save restaurant. Please try again.');
    } finally {
      setIsSavingRestaurant(false);
    }
  };

  const handleContactToggle = (contactId: string) => {
    setSelectedContactIds(prev =>
      prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId]
    );
  };

  const handleClearResults = () => {
    setMenuPreviewText('');
    setPasteText('');
    setUploadedFile(null);
    setMenuUrl('');
    setExtractError(null);
    setParsedDishes([]);
    setExtractedMenuId(null);
    stopExtractPoll();
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImagePreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Confidence pill color
  const getConfidenceColor = (c: number) => {
    if (c >= 0.9) return 'bg-green-100 text-green-800 border-green-300';
    if (c >= 0.7) return 'bg-[#A5CFDD]/20 text-[#2A5F6F] border-[#A5CFDD]';
    return 'bg-amber-100 text-amber-800 border-amber-300';
  };

  // Loading overlay
  if (isCreatingQuote) {
    return (
      <div className="fixed inset-0 bg-white/90 z-50 flex flex-col items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#A5CFDD] mb-4" />
        <p className="text-lg font-medium text-[#2A2A2A]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          {['Reading menu...', 'Extracting ingredients...', 'Matching to catalog...', 'Building your quote...', 'Almost there...', 'So close...'][loadingPhase]}
        </p>
        <p className="text-sm text-gray-400 mt-2">This usually takes 15 to 60 seconds</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-5xl mx-auto">

        {quotaBlockMessage && (
          <div className="mx-0 mt-0 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm font-medium text-red-800 mb-1">Quote limit reached</p>
            <p className="text-sm text-red-700">{quotaBlockMessage}</p>
          </div>
        )}

        {!quotaBlockMessage && error && (
          <div className="mx-0 mt-0 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-2">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {backgroundProcessing && (
          <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-[#A5CFDD]/30">
            <div className="flex items-start gap-3">
              <div className="mt-1 w-2 h-2 rounded-full bg-[#A5CFDD] animate-pulse shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[#2A2A2A]">
                  Menu looks good, we're working on it now.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  We will notify you when it's ready.
                </p>
                {/* Pulsing teal bar */}
                <div className="mt-3 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full w-1/3 rounded-full bg-[#A5CFDD]"
                    style={{
                      animation: 'pulse-slide 2s ease-in-out infinite',
                    }}
                  />
                </div>
                <style>{`
                  @keyframes pulse-slide {
                    0% { transform: translateX(-100%); opacity: 0.6; }
                    50% { transform: translateX(100%); opacity: 1; }
                    100% { transform: translateX(300%); opacity: 0.6; }
                  }
                `}</style>
              </div>
            </div>
          </div>
        )}

        {/* ── Header ── */}
        <div className="mb-8">
          <h1
            className="text-3xl md:text-4xl mb-2"
            style={{ fontFamily: "'Playfair Display', serif", color: '#A5CFDD' }}
          >
            {liquorDemo ? 'Start New Beverage Quote' : 'Start New Quote'}
          </h1>
          <p className="text-gray-600" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            {liquorDemo ? 'Paste a drink menu, upload a photo, or type your list.' : 'Paste a menu, cocktail list, or food concept: anything a chef or operator would hand a rep.'}
          </p>

          {/* Five-step guide — demo flow */}
          {isDemoMode() && (
            <div className="mt-4 bg-[#A5CFDD]/10 border border-[#A5CFDD]/30 rounded-lg px-5 py-4">
              <p className="text-xs font-semibold text-[#2A5F6F] mb-2 uppercase tracking-wide">How it works</p>
              <ol className="list-decimal list-inside text-sm text-[#2A2A2A] space-y-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                <li>Drop in the restaurant's menu</li>
                <li>QuoteMe matches ingredients to your catalog</li>
                <li>Dial in the quote if needed</li>
                <li>Send the quote to the chef</li>
                <li>Turn the quote into the order guide</li>
              </ol>
            </div>
          )}
        </div>

        {/* Fix 131 + Fix 139: Draft limit block.
            The block used to offer "or allow unlimited drafts", which PATCHed
            unlimited_drafts on /users/me. That param is stripped server-side for
            every self-service caller regardless of role (users_controller
            "Param hardening": only quoteme_admin may set it), and the PATCH
            still returns 200 with a full user body. res.data was therefore
            truthy, the success branch fired, a green "Unlimited drafts enabled"
            banner rendered and the upload zone unlocked -- then menus#create
            re-checked the same flag and returned 422 on that exact limit one
            action later. The escape hatch never worked, so it is gone. The one
            action that does clear the wall -- finish or delete a draft -- is the
            link that was already sitting beside it. */}
        {draftLimitReached && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800">
              You have 2 quotes in progress.{' '}
              <button
                onClick={() => navigate('/rep/quotes')}
                className="text-[#A5CFDD] hover:text-[#7FAEC2] font-medium underline underline-offset-2"
              >
                Go to your quotes &rarr;
              </button>
              {' '}to finish or delete one before starting a new one.
            </p>
          </div>
        )}

        {/* ── Upload Zone ── */}
        <div className={`bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100 ${draftLimitReached ? 'opacity-40 pointer-events-none' : ''}`}>
          <h2 className="text-lg font-semibold text-[#2A2A2A] mb-1">Menu</h2>
          <p className="text-xs text-gray-500 mb-4">
            Upload the restaurant's menu: dishes and drinks, not a product catalog or price list.
            Uploading a catalog instead? Use the Catalog section below.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,.pdf,.png,.jpg,.jpeg,.gif,.webp"
            className="hidden"
            onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileSelect(file); }}
          />
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileSelect(file); }}
          />

          {/* Camera button — mobile only */}
          {hasCamera && (
            <button
              onClick={() => cameraInputRef.current?.click()}
              disabled={isExtracting}
              className="w-full flex items-center justify-center gap-3 mb-4 py-3 rounded-lg border-2 border-[#A5CFDD] bg-white hover:bg-[#A5CFDD]/10 transition-colors md:hidden"
            >
              <Camera className="w-5 h-5 text-[#A5CFDD]" />
              <span className="text-sm font-medium text-[#A5CFDD]">Take Photo of Menu</span>
            </button>
          )}

          {/* Drag/drop zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
              isDragging ? 'border-[#7FAEC2] bg-[#A5CFDD]/10' : 'border-[#A5CFDD] hover:border-[#7FAEC2]'
            }`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {isExtracting ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-6 h-6 animate-spin text-[#A5CFDD]" />
                <p className="text-sm text-gray-600">Reading menu...</p>
              </div>
            ) : (
              <>
                <Upload className="w-6 h-6 text-[#A5CFDD] mx-auto mb-2" />
                <p className="text-sm text-gray-700 mb-1">Drag files here or click to browse</p>
                <p className="text-xs text-gray-400">PDF, image, CSV, or text file</p>
              </>
            )}
          </div>

          {uploadedFile && (
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-3">
              <FileText className="w-3.5 h-3.5" />
              <span>{uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(1)} KB)</span>
            </div>
          )}

          {extractError && (
            <div className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-md p-3 mt-4">
              {extractError}
            </div>
          )}
        </div>


        {/* ── Menu URL Row ── */}
        <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100">
          <div className="max-w-lg">
              <Label className="text-sm mb-2 block text-gray-700 font-medium">Menu URL</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="www.example.com/menu"
                  className="bg-gray-50 flex-1"
                  value={menuUrl}
                  onChange={(e) => setMenuUrl(e.target.value)}
                />
                <Button
                  className="bg-[#A5CFDD] hover:bg-[#7FAEC2] text-white shrink-0"
                  onClick={handleUrlExtract}
                  disabled={!menuUrl.trim() || isExtracting}
                >
                  {isExtracting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fetch'}
                </Button>
              </div>
              <button
                className={`text-xs text-[#A5CFDD] hover:text-[#7FAEC2] mt-1.5${!selectedRestaurant ? ' opacity-50 cursor-not-allowed' : ''}`}
                title={!selectedRestaurant ? "Select a customer first to link their profile URL" : undefined}
                onClick={() => {
                  if (selectedRestaurant?.website) setMenuUrl(selectedRestaurant.website);
                }}
                disabled={!selectedRestaurant}
              >
                Click to Link from Customer Profile
              </button>
          </div>
        </div>

        {/* ── OR PASTE TEXT Divider ── */}
        <div className="relative flex py-4 items-center mb-6">
          <div className="flex-grow border-t border-gray-300" />
          <span className="flex-shrink-0 mx-4 text-gray-400 text-xs uppercase tracking-wide font-medium">
            OR PASTE TEXT
          </span>
          <div className="flex-grow border-t border-gray-300" />
        </div>

        {/* ── Side-by-side Panels ── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6 overflow-hidden">
          <div className="flex flex-col md:flex-row">
            {/* Left Panel — Menu Text (45%) */}
            <div className="w-full md:w-[45%] p-6 md:border-r border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <Pencil className="w-4 h-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-700">Menu Text</h3>
              </div>
              {uploadedFile && !pasteText ? (
                <div className="border border-gray-200 rounded-lg min-h-[300px] bg-gray-50 flex flex-col items-center justify-center text-center overflow-hidden">
                  {imagePreviewUrl ? (
                    <div className="w-full">
                      <img
                        src={imagePreviewUrl}
                        alt="Menu preview"
                        className="w-full max-h-[280px] object-contain"
                      />
                      <div className="p-3 border-t border-gray-200 bg-white">
                        <p className="text-xs text-gray-500">{uploadedFile.name}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6">
                      <FileText className="w-10 h-10 text-[#A5CFDD] mb-3 mx-auto" />
                      <p className="text-sm text-gray-600 font-medium">{uploadedFile.name}</p>
                      <p className="text-xs text-gray-400 mt-1">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  )}
                  {isExtracting && (
                    <div className="p-3 flex items-center gap-2 justify-center bg-white border-t border-gray-200 w-full">
                      <Loader2 className="w-4 h-4 animate-spin text-[#A5CFDD]" />
                      <span className="text-xs text-gray-500">Extracting text...</span>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* BUG #27 — hidden for Wednesday: this promised a concept-to-quote
                      feature that doesn't exist yet. Re-enable when it ships. */}
                  {false && !conceptTipDismissed && (
                    <div className="mb-3 p-3 bg-[#A5CFDD]/10 border border-[#A5CFDD]/30 rounded-lg flex items-start gap-2">
                      <Lightbulb className="w-4 h-4 text-[#2A5F6F] mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-[#2A5F6F]">
                          <span className="font-medium">No menu yet?</span> Just type a concept like "Italian deli" or "sushi restaurant" and we'll build a starter quote with typical ingredients.
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setConceptTipDismissed(true);
                          localStorage.setItem('quoteme_concept_tip_dismissed', 'true');
                        }}
                        className="text-[#2A5F6F]/50 hover:text-[#2A5F6F] shrink-0"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  <Textarea
                    className="bg-gray-50 min-h-[300px] resize-none text-sm"
                    placeholder="Paste a menu, cocktail list, or food concept, anything a chef or operator would hand a rep."
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    maxLength={5000}
                  />
                  <div className="text-right mt-1">
                    <span className="text-xs text-gray-400">
                      {pasteText.length} / 5,000 characters
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Right Panel — Extracted Ingredients (55%) */}
            <div className="w-full md:w-[55%] p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Extracted Ingredients</h3>

              {isExtractingPreview ? (
                /* Skeleton loading */
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
                      <div className="flex flex-wrap gap-2">
                        {[1, 2, 3, 4].map(j => (
                          <div key={j} className="h-7 bg-gray-100 rounded-full w-20" />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : parsedDishes.length > 0 ? (
                <div className="space-y-5">
                  {parsedDishes.map(dish => (
                    <div key={dish.id}>
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-sm font-medium text-[#2A2A2A]">{dish.name}</h4>
                        <span className="text-xs text-gray-400">({dish.ingredients.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {dish.ingredients.map(ing => (
                          editingIngredient === ing.id ? (
                            <div key={ing.id} className="flex items-center gap-1">
                              <input
                                type="text"
                                className="text-xs border border-[#A5CFDD] rounded-full px-3 py-1 w-32 focus:outline-none focus:ring-1 focus:ring-[#A5CFDD]"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveEdit(dish.id, ing.id);
                                  if (e.key === 'Escape') setEditingIngredient(null);
                                }}
                                autoFocus
                                onBlur={() => handleSaveEdit(dish.id, ing.id)}
                              />
                            </div>
                          ) : (
                            <span
                              key={ing.id}
                              className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border cursor-pointer transition-colors hover:shadow-sm ${getConfidenceColor(ing.confidence)}`}
                              onClick={() => handleStartEdit(ing.id, ing.name)}
                            >
                              {ing.name}
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRemoveIngredient(dish.id, ing.id); }}
                                className="ml-0.5 hover:text-red-500 transition-colors"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          )
                        ))}
                      </div>
                      {addingToDish === dish.id ? (
                        <div className="flex items-center gap-1 mt-2">
                          <input
                            type="text"
                            className="text-xs border border-gray-300 rounded-full px-3 py-1 w-40 focus:outline-none focus:ring-1 focus:ring-[#A5CFDD]"
                            placeholder="Ingredient name"
                            value={newIngredientName}
                            onChange={(e) => setNewIngredientName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); handleAddIngredient(dish.id); }
                              if (e.key === 'Escape') { setAddingToDish(null); setNewIngredientName(''); }
                            }}
                            onBlur={() => { if (!newIngredientName.trim()) { setAddingToDish(null); setNewIngredientName(''); } }}
                            autoFocus
                          />
                          <button
                            onClick={() => handleAddIngredient(dish.id)}
                            className="text-xs text-[#A5CFDD] hover:text-[#7FAEC2] font-medium"
                          >
                            Add
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setAddingToDish(dish.id)}
                          className="text-xs text-[#A5CFDD] hover:text-[#7FAEC2] mt-2"
                        >
                          + Add ingredient
                        </button>
                      )}
                    </div>
                  ))}
                  {/* Add Dish */}
                  {addingNewDish ? (
                    <div className="flex items-center gap-1 mt-4 pt-4 border-t border-gray-100">
                      <input
                        type="text"
                        className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 w-48 focus:outline-none focus:ring-1 focus:ring-[#A5CFDD]"
                        placeholder="Dish name"
                        value={newDishName}
                        onChange={(e) => setNewDishName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); handleAddDish(); }
                          if (e.key === 'Escape') { setAddingNewDish(false); setNewDishName(''); }
                        }}
                        onBlur={() => { if (!newDishName.trim()) { setAddingNewDish(false); setNewDishName(''); } }}
                        autoFocus
                      />
                      <button
                        onClick={handleAddDish}
                        className="text-sm text-[#A5CFDD] hover:text-[#7FAEC2] font-medium px-2"
                      >
                        Add
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setAddingNewDish(true)}
                      className="text-sm text-[#A5CFDD] hover:text-[#7FAEC2] mt-4 pt-4 border-t border-gray-100 w-full text-left font-medium"
                    >
                      + Add Dish
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center min-h-[300px]">
                  <p className="text-gray-400 text-sm">Your parsed menu will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Service Busy Banner */}
        {serviceBusy && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
            <p className="text-sm text-amber-800">
              Our menu analysis service is temporarily busy. This usually resolves in a few seconds.
            </p>
            <Button
              onClick={handleRetry}
              size="sm"
              className="ml-4 bg-[#A5CFDD] hover:bg-[#7FAEC2] text-white flex-shrink-0"
            >
              <RefreshCw size={14} className="mr-1" /> Try Again
            </Button>
          </div>
        )}

        {/* ── Action Bar ── */}
        <div className="flex flex-col items-center gap-3 mb-8">
          <div className="flex flex-col sm:flex-row justify-center gap-3 w-full">
            <Button
              className="bg-[#F2993D] hover:bg-[#E8953A] text-white px-6"
              onClick={handleSkipToExport}
              disabled={isCreatingQuote || draftLimitReached || (!pasteText && !menuPreviewText)}
            >
              Skip to Quote
            </Button>
            <Button
              className="bg-[#A5CFDD] hover:bg-[#7FAEC2] text-white px-8 py-2.5 text-base font-medium"
              onClick={handleContinueToQuoteBuilder}
              disabled={isCreatingQuote || draftLimitReached || (!pasteText && !menuPreviewText)}
            >
              Match to Catalog
            </Button>
            <Button
              variant="outline"
              className="border-gray-300 text-gray-600 hover:bg-gray-50"
              onClick={handleClearResults}
              disabled={!hasExtractedResults({ parsedDishCount: parsedDishes.length, pasteText, menuPreviewText, hasUploadedFile: !!uploadedFile })}
            >
              Clear Results
            </Button>
          </div>
          {/* B-189 — surface the error next to the button that was pressed, not
              only in the top-of-page banner far above the fold. */}
          {error && (
            <div
              ref={actionErrorRef}
              className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-center max-w-md"
            >
              {error}
            </div>
          )}
        </div>

        {/* ── Customer Information ── */}
        <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100">
          {isDemoMode() ? (
            <>
              <div className="flex items-center gap-3 mb-3">
                <h2 className="text-lg font-semibold text-[#2A2A2A]">Customer Information</h2>
                <span className="text-xs px-3 py-1 bg-[#A5CFDD]/20 text-[#2A5F6F] rounded-full font-medium">
                  Open Quote - no restaurant selected
                </span>
              </div>
              <div className="text-center py-4 text-gray-400">
                <p className="text-sm">Demo Mode - Log in to tie quote to restaurant</p>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-[#2A2A2A]">Customer Information</h2>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-[#A5CFDD] border-[#A5CFDD] hover:bg-[#A5CFDD]/10 text-sm"
                  onClick={() => setIsQuoteOpened(!isQuoteOpened)}
                >
                  {isQuoteOpened ? 'Select Customer' : 'Open Quote'}
                </Button>
              </div>

              {isQuoteOpened ? (
                <div className="text-center py-6 text-gray-400">
                  <p className="text-sm">Open Quote - No Customer Details Needed</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label htmlFor="restaurant" className="text-sm mb-2 block">Customer</Label>
                      {/* BUG #4b — controlled value so the dropdown reflects the
                          restaurant selected/created after Save (was uncontrolled). */}
                      <select
                        id="restaurant"
                        className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm"
                        value={selectedRestaurant?.id || ''}
                        onChange={handleRestaurantChange}
                      >
                        <option value="">Select a customer</option>
                        {restaurants.map(restaurant => (
                          <option key={restaurant.id} value={restaurant.id}>{restaurant.name}</option>
                        ))}
                        <option value="add-new">+ Add new</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="group" className="text-sm mb-2 block">Customer Group</Label>
                      <select
                        id="group"
                        className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm"
                        onChange={(e) => {
                          if (e.target.value === 'add-new') { setIsAddGroupOpen(true); e.target.value = ''; }
                        }}
                      >
                        <option>Select a group</option>
                        <option value="add-new">+ Add new</option>
                      </select>
                    </div>
                  </div>

                  {/* Contacts */}
                  {selectedRestaurant && (
                    <div className="border border-gray-200 rounded-md p-4 bg-gray-50 mb-4">
                      <div className="flex justify-between items-center mb-3">
                        <Label className="text-sm font-medium text-gray-700">Select Contacts for Quote</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-[#A5CFDD] hover:bg-[#A5CFDD]/10 text-xs px-2 border border-[#A5CFDD]"
                          onClick={() => setIsAddContactOpen(true)}
                        >
                          Add Contact
                        </Button>
                      </div>
                      <div className="space-y-3">
                        {selectedRestaurant.contacts.map((contact) => (
                          <div key={contact.id} className="flex items-start space-x-3 bg-white p-3 rounded border border-gray-100 shadow-sm">
                            <Checkbox
                              id={contact.id}
                              checked={selectedContactIds.includes(contact.id)}
                              onCheckedChange={() => handleContactToggle(contact.id)}
                              className="mt-1 border-gray-300 data-[state=checked]:bg-[#A5CFDD] data-[state=checked]:border-[#A5CFDD]"
                            />
                            <div className="flex-1">
                              <div className="flex justify-between">
                                <label htmlFor={contact.id} className="text-sm font-medium text-gray-900 cursor-pointer">
                                  {contact.first_name} {contact.last_name}
                                </label>
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full border border-gray-200">
                                  {contact.role}
                                </span>
                              </div>
                              <div className="mt-1 text-xs text-gray-500 grid grid-cols-2 gap-2">
                                <span>{contact.email}</span>
                                <span>{contact.phone}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Selected restaurant details */}
                  {selectedRestaurant ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm mb-1 block text-gray-500">Restaurant Name</Label>
                        <p className="text-sm text-[#2A2A2A]">{selectedRestaurant.name}</p>
                      </div>
                      <div>
                        <Label className="text-sm mb-1 block text-gray-500">Address</Label>
                        <p className="text-sm text-[#2A2A2A]">{selectedRestaurant.address_line_1 || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-sm mb-1 block text-gray-500">City</Label>
                        <p className="text-sm text-[#2A2A2A]">{selectedRestaurant.city || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-sm mb-1 block text-gray-500">State</Label>
                        <p className="text-sm text-[#2A2A2A]">{selectedRestaurant.state || '-'}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 text-gray-400">
                      <p className="text-sm">Select a customer to view details</p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* ── Catalog Section ── */}
        <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold text-[#2A2A2A] mb-1">Catalog</h2>

          <div className="mb-4">
            {isGuest && catalogs.length === 0 ? (
              <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                <div className="flex items-center gap-3">
                  <span className="text-green-600 text-lg">&#10003;</span>
                  <div>
                    <p className="text-sm font-medium text-[#2A2A2A]">Sample catalog active</p>
                    <p className="text-xs text-gray-500">Upload your own catalog below, or continue with the sample.</p>
                  </div>
                </div>
              </div>
            ) : catalogLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-3">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading catalogs...
              </div>
            ) : catalogs.length > 0 ? (
              <div className="space-y-2">
                {catalogs.filter(c => c.status === 'active').slice(0, 1).map(cat => (
                  <div key={cat.id} className="border border-green-200 rounded-lg p-4 flex items-center justify-between bg-green-50">
                    <div className="flex items-center gap-3">
                      <span className="text-green-600 text-lg">&#10003;</span>
                      <div>
                        <p className="text-sm font-medium text-[#2A2A2A]">
                          Active Catalog: {cleanCatalogName(cat)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {cat.row_count.toLocaleString()} products &middot; uploaded {new Date(cat.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setCatalogUploadExpanded(!catalogUploadExpanded)}
                      className="text-xs text-[#A5CFDD] hover:text-[#7FAEC2] flex items-center gap-1"
                    >
                      {catalogUploadExpanded ? (
                        <>Hide <ChevronUp className="w-3 h-3" /></>
                      ) : (
                        <>Upload Custom Catalog <ChevronDown className="w-3 h-3" /></>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-amber-200 rounded-lg p-4 bg-amber-50">
                <div className="flex items-center gap-3">
                  <span className="text-amber-500 text-lg">&#9888;</span>
                  <div>
                    <p className="text-sm font-medium text-[#2A2A2A]">Sample catalog active</p>
                    <p className="text-xs text-gray-500">Prices won't reflect your rep's pricing.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Upload section */}
          <div className={!catalogUploadExpanded ? 'hidden' : ''}>
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              {catalogs.length > 0 ? 'Upload New Catalog' : 'Upload Your Catalog'}
            </h3>
            <input
              ref={catalogFileInputRef}
              type="file"
              accept=".csv,.xlsx"
              className="hidden"
              onChange={(e) => { const file = e.target.files?.[0]; if (file) handleCatalogFileSelect(file); e.target.value = ''; }}
            />
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDraggingCatalog ? 'border-[#7FAEC2] bg-[#A5CFDD]/10' : 'border-[#A5CFDD] hover:border-[#7FAEC2]'
              }`}
              onClick={() => !catalogUploading && catalogFileInputRef.current?.click()}
              onDragOver={handleCatalogDragOver}
              onDragLeave={handleCatalogDragLeave}
              onDrop={handleCatalogDrop}
            >
              {catalogUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-6 h-6 animate-spin text-[#A5CFDD]" />
                  <p className="text-sm text-gray-500">Uploading and processing catalog...</p>
                </div>
              ) : (
                <>
                  <Upload className="w-6 h-6 text-[#A5CFDD] mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Drag a file here or click to browse</p>
                  <p className="text-xs text-gray-400 mt-1">CSV or Excel (.csv, .xlsx)</p>
                </>
              )}
            </div>
            {/* Upload result / error */}
            {catalogUploadResult?.isError && (
              <div className="mt-3 p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200">
                {catalogUploadResult.message}
              </div>
            )}

            {/* Phased progress tracker */}
            {uploadPhase && !catalogUploadResult?.isError && (
              <div className="mt-3 space-y-2">
                {/* Phase 1: Import */}
                <div className="flex items-center gap-2 text-sm">
                  {uploadPhase === 'uploading' ? (
                    <Loader2 className="w-4 h-4 animate-spin text-[#A5CFDD]" />
                  ) : (
                    <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    </div>
                  )}
                  <span className={uploadPhase === 'uploading' ? 'text-gray-600' : 'text-green-700'}>
                    {uploadPhase === 'uploading' ? 'Importing products...' : catalogUploadResult?.message || 'Products imported'}
                  </span>
                </div>

                {/* Phase 2: AI Classification */}
                {(uploadPhase === 'classifying' || uploadPhase === 'complete') && classificationProgress.total > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    {uploadPhase === 'classifying' ? (
                      <Loader2 className="w-4 h-4 animate-spin text-[#A5CFDD]" />
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      </div>
                    )}
                    <span className={uploadPhase === 'classifying' ? 'text-gray-600' : 'text-green-700'}>
                      {uploadPhase === 'classifying'
                        ? `Adding categories... ${classificationProgress.progress}/${classificationProgress.total}`
                        : `Categories added for ${classificationProgress.total} products`}
                    </span>
                  </div>
                )}

                {/* Progress bar during classification */}
                {uploadPhase === 'classifying' && classificationProgress.total > 0 && (
                  <div className="ml-6 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#A5CFDD] rounded-full transition-all duration-500"
                      style={{ width: `${Math.round((classificationProgress.progress / classificationProgress.total) * 100)}%` }}
                    />
                  </div>
                )}

                {/* Phase 3: Review needed */}
                {uploadPhase === 'complete' && classificationFlagged > 0 && (
                  <div className="p-3 rounded-lg text-sm bg-amber-50 text-amber-800 border border-amber-200 flex items-center justify-between">
                    <span>{classificationFlagged} product{classificationFlagged === 1 ? '' : 's'} need category review</span>
                    <button
                      onClick={() => catalogUploadResult?.catalogId && setShowCategoryReview(true)}
                      className="text-xs font-medium px-3 py-1 bg-amber-100 hover:bg-amber-200 rounded transition-colors"
                    >
                      Review
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* ── Drawers ── */}

      {/* Category Review Drawer */}
      {showCategoryReview && catalogUploadResult?.catalogId && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowCategoryReview(false)} />
          <div className="fixed right-0 top-0 h-full w-full sm:w-[560px] bg-white shadow-xl z-50 overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg text-[#2A2A2A]">Review Product Categories</h2>
              <button onClick={() => setShowCategoryReview(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-4">
                Review these categories: classification was uncertain.
              </p>
              <CategoryReviewPanel
                catalogId={catalogUploadResult.catalogId}
                onDone={(remaining) => {
                  setShowCategoryReview(false);
                  setClassificationFlagged(remaining);
                }}
              />
            </div>
          </div>
        </>
      )}

      {/* Add Restaurant Drawer */}
      {isAddRestaurantOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsAddRestaurantOpen(false)} />
          <div className="fixed right-0 top-0 h-full w-full sm:w-[500px] bg-white shadow-xl z-50 overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl text-[#2A2A2A]">Add New Restaurant</h2>
              <button onClick={() => setIsAddRestaurantOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label htmlFor="new-restaurant-name" className="text-sm mb-2 block">Restaurant Name *</Label>
                <Input id="new-restaurant-name" type="text" placeholder="Enter restaurant name" className="bg-gray-50" />
              </div>
              <div>
                <Label htmlFor="new-business-name" className="text-sm mb-2 block">Business Name</Label>
                <Input id="new-business-name" type="text" placeholder="Enter business name" className="bg-gray-50" />
              </div>
              <div>
                <Label htmlFor="new-restaurant-group" className="text-sm mb-2 block">Restaurant Group</Label>
                <select
                  id="new-restaurant-group"
                  className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm"
                  onChange={(e) => { if (e.target.value === 'add-new') { setIsAddGroupOpen(true); e.target.value = ''; } }}
                >
                  <option>Select a group</option>
                  <option value="add-new">+ Add new Group</option>
                </select>
              </div>
              <div>
                <Label htmlFor="new-contact-name" className="text-sm mb-2 block">Contact Name</Label>
                <Input id="new-contact-name" type="text" placeholder="Enter contact name" className="bg-gray-50" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="new-email" className="text-sm mb-2 block">Email *</Label>
                  <Input id="new-email" type="email" placeholder="email@example.com" className="bg-gray-50" />
                </div>
                <div>
                  <Label htmlFor="new-phone" className="text-sm mb-2 block">Phone</Label>
                  <Input id="new-phone" type="tel" placeholder="(555) 555-5555" className="bg-gray-50" />
                </div>
              </div>
              <div>
                <Label htmlFor="new-street-address" className="text-sm mb-2 block">Street Address</Label>
                <Input id="new-street-address" type="text" placeholder="123 Main St" className="bg-gray-50" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="new-city" className="text-sm mb-2 block">City</Label>
                  <Input id="new-city" type="text" placeholder="City" className="bg-gray-50" />
                </div>
                <div>
                  <Label htmlFor="new-state" className="text-sm mb-2 block">State</Label>
                  <Input id="new-state" type="text" placeholder="State" className="bg-gray-50" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="new-zip" className="text-sm mb-2 block">ZIP Code</Label>
                  <Input id="new-zip" type="text" placeholder="12345" className="bg-gray-50" />
                </div>
                <div>
                  <Label htmlFor="new-country" className="text-sm mb-2 block">Country</Label>
                  <Input id="new-country" type="text" placeholder="USA" className="bg-gray-50" />
                </div>
              </div>
              <div>
                <Label htmlFor="new-website" className="text-sm mb-2 block">Website</Label>
                <Input id="new-website" type="text" placeholder="www.example.com" className="bg-gray-50" />
              </div>
              <div>
                <Label htmlFor="new-restaurant-type" className="text-sm mb-2 block">Restaurant Type</Label>
                <select id="new-restaurant-type" className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm">
                  <option>Select type</option>
                  <option>Fine Dining</option>
                  <option>Casual Dining</option>
                  <option>Fast Casual</option>
                  <option>Cafe</option>
                  <option>Bar & Grill</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <Label htmlFor="new-cuisine" className="text-sm mb-2 block">Cuisine Type</Label>
                <Input id="new-cuisine" type="text" placeholder="e.g., Italian, Japanese, American" className="bg-gray-50" />
              </div>
              <div>
                <Label htmlFor="new-notes" className="text-sm mb-2 block">Notes</Label>
                <Textarea id="new-notes" placeholder="Additional notes about the restaurant" className="bg-gray-50 h-24" />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setIsAddRestaurantOpen(false)} disabled={isSavingRestaurant}>Cancel</Button>
              <Button className="bg-[#A5CFDD] hover:bg-[#7FAEC2] text-white" onClick={handleSaveRestaurant} disabled={isSavingRestaurant}>
                {isSavingRestaurant ? 'Saving…' : 'Save Restaurant'}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Add Contact Drawer */}
      {isAddContactOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsAddContactOpen(false)} />
          <div className="fixed right-0 top-0 h-full w-full sm:w-[500px] bg-white shadow-xl z-50 overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl text-[#2A2A2A]">Add New Contact</h2>
              <button onClick={() => setIsAddContactOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label htmlFor="contact-name" className="text-sm mb-2 block">Name *</Label>
                <Input id="contact-name" type="text" placeholder="Enter contact name" className="bg-gray-50" />
              </div>
              <div>
                <Label htmlFor="contact-email" className="text-sm mb-2 block">Contact Email *</Label>
                <Input id="contact-email" type="email" placeholder="email@example.com" className="bg-gray-50" />
              </div>
              <div>
                <Label htmlFor="contact-phone" className="text-sm mb-2 block">Contact Phone</Label>
                <Input id="contact-phone" type="tel" placeholder="(555) 555-5555" className="bg-gray-50" />
              </div>
              <div>
                <Label htmlFor="contact-position" className="text-sm mb-2 block">Position</Label>
                <Input id="contact-position" type="text" placeholder="e.g., Head Chef, Manager, Owner" className="bg-gray-50" />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setIsAddContactOpen(false)}>Cancel</Button>
              <Button className="bg-[#A5CFDD] hover:bg-[#7FAEC2] text-white" onClick={() => setIsAddContactOpen(false)}>
                Save Contact
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Add Group Drawer */}
      {isAddGroupOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsAddGroupOpen(false)} />
          <div className="fixed right-0 top-0 h-full w-full sm:w-[500px] bg-white shadow-xl z-50 overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl text-[#2A2A2A]">Add New Restaurant Group</h2>
              <button onClick={() => setIsAddGroupOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label htmlFor="group-name" className="text-sm mb-2 block">Group Name *</Label>
                <Input id="group-name" type="text" placeholder="Enter group name" className="bg-gray-50" />
              </div>
              <div>
                <Label htmlFor="group-primary-contact-name" className="text-sm mb-2 block">Primary Contact Name *</Label>
                <Input id="group-primary-contact-name" type="text" placeholder="Enter primary contact name" className="bg-gray-50" />
              </div>
              <div>
                <Label htmlFor="group-primary-contact-email" className="text-sm mb-2 block">Primary Contact Email *</Label>
                <Input id="group-primary-contact-email" type="email" placeholder="email@example.com" className="bg-gray-50" />
              </div>
              <div>
                <Label htmlFor="group-primary-contact-phone" className="text-sm mb-2 block">Primary Contact Phone</Label>
                <Input id="group-primary-contact-phone" type="tel" placeholder="(555) 555-5555" className="bg-gray-50" />
              </div>
              <div>
                <Label htmlFor="group-office-address" className="text-sm mb-2 block">Office Address *</Label>
                <Input id="group-office-address" type="text" placeholder="123 Main St, City, State, ZIP" className="bg-gray-50" />
              </div>
              <div>
                <Label htmlFor="group-restaurant-types" className="text-sm mb-2 block">Restaurant Types</Label>
                <select id="group-restaurant-types" className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-sm">
                  <option>Select type</option>
                  <option>Restaurant</option>
                  <option>Hotel</option>
                  <option>Catering</option>
                  <option>Venue</option>
                  <option>Healthcare</option>
                  <option>Multi-Unit</option>
                </select>
              </div>
              <div>
                <Label htmlFor="group-internal-notes" className="text-sm mb-2 block">Internal Rep Notes</Label>
                <Textarea id="group-internal-notes" placeholder="Internal notes for sales reps" className="bg-gray-50 h-24" />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setIsAddGroupOpen(false)}>Cancel</Button>
              <Button className="bg-[#A5CFDD] hover:bg-[#7FAEC2] text-white" onClick={() => setIsAddGroupOpen(false)}>
                Save Group
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Upgrade Drawer */}
      <UpgradeDrawer isOpen={isUpgradeDrawerOpen} onClose={() => setIsUpgradeDrawerOpen(false)} />
    </div>
  );
}
